'use client';

import { useState, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';

interface ShareUrlModalProps {
  open: boolean;
  onClose: () => void;
  shareUrl: string;
  accentPrimary: string;
  accentSecondary: string;
  isDark?: boolean;
}

export function ShareUrlModal({
  open,
  onClose,
  shareUrl,
  accentPrimary,
  accentSecondary,
  isDark = true,
}: ShareUrlModalProps) {
  const t = useTranslations('Celebration');
  const [copied, setCopied] = useState(false);
  // Resolve client-only capability after mount so SSR and first render agree
  // (server has no `navigator`) — avoids a React #418 hydration mismatch.
  const [canNativeShare, setCanNativeShare] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setCopied(false);
  }, [open]);

  useEffect(() => {
    setCanNativeShare(typeof navigator !== 'undefined' && !!navigator.share);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      inputRef.current?.select();
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback: select text for manual copy
      inputRef.current?.select();
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ url: shareUrl });
      } catch {
        // User cancelled
      }
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className={`relative z-10 mx-auto w-full max-w-md rounded-t-2xl p-5 sm:rounded-2xl ${isDark ? 'bg-card border-border border' : 'border-border border bg-white'}`}
        style={{
          boxShadow: isDark ? `0 0 60px ${accentPrimary}15` : '0 20px 60px rgba(0,0,0,0.15)',
        }}
      >
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-foreground text-lg font-bold">{t('shareEvent')}</h3>
          <button
            onClick={onClose}
            className="text-muted hover:bg-surface hover:text-foreground flex h-8 w-8 items-center justify-center rounded-full"
          >
            ✕
          </button>
        </div>

        {/* URL input + copy */}
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            readOnly
            value={shareUrl}
            onClick={() => inputRef.current?.select()}
            className="border-border bg-surface text-foreground flex-1 rounded-lg border px-3 py-2.5 text-[13px] outline-none focus:ring-1"
            style={{ focusRingColor: accentPrimary } as React.CSSProperties}
          />
          <button
            onClick={handleCopy}
            className="shrink-0 rounded-lg px-4 py-2.5 text-[13px] font-semibold transition-transform hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: copied
                ? isDark
                  ? '#059669'
                  : '#059669'
                : `linear-gradient(135deg, ${accentPrimary}, ${accentSecondary})`,
              color: isDark ? '#0b0d0e' : '#ffffff',
            }}
          >
            {copied ? t('copied') : t('copyLink')}
          </button>
        </div>

        {/* Native share (mobile) */}
        {canNativeShare && (
          <button
            onClick={handleNativeShare}
            className="border-border bg-surface text-foreground hover:bg-surface/80 mt-3 w-full rounded-lg border py-2.5 text-[13px] font-semibold transition-colors"
          >
            {t('shareVia')}
          </button>
        )}
      </div>
    </div>
  );
}
